import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { useAppStore } from "@src/AppStore";
import { apiProjectShiftCosts, ShiftCostItem, ShiftCostSummary } from "@src/apis/project-shift-costs";
import { apiDepotSearch } from "@src/apis/depot-search";
import moment from "moment";

type DepotOption = { label: string; value: string };
type StateOption = { label: string; value: string };
type ProjectOption = { label: string; value: string };
type MarginType = "" | "no_margin" | "positive" | "negative";

interface ShiftDayData {
  shift_id: number;
  drs_summary_id: number;
  day: number;
  revenue: number | null;
  cost: number;
  margin: number;
  isActual: boolean; // false = planned (rendered dashed)
}

interface ProjectRowData {
  rowIndex: number;
  dayShifts: Map<number, ShiftDayData>; // day -> shift data for this row
}

interface ProjectData {
  project_id: string;
  project_name: string;
  rows: ProjectRowData[];
  mtdRevenue: number;
  mtdCost: number;
  mtdMargin: number;
}

const MONTHS = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

// Reports available from November 2025 onwards
const START_YEAR = 2025;
const START_MONTH = 11; // November

const getAvailableYears = () => {
  const currentYear = moment().year();
  const years = [];
  for (let year = START_YEAR; year <= currentYear + 1; year++) {
    years.push(year);
  }
  return years;
};

const getAvailableMonths = (selectedYear: number) => {
  return MONTHS.filter((month) => {
    // For start year (2025), only show months from November onwards
    if (selectedYear === START_YEAR && month.value < START_MONTH) {
      return false;
    }
    // For future years, show all months
    return true;
  });
};

const calculateMonthOffset = (year: number, month: number): number => {
  const currentDate = moment();
  const selectedDate = moment({ year, month: month - 1 }); // month is 1-indexed, moment expects 0-indexed
  return selectedDate.diff(currentDate, "months");
};

const formatNumber = (value: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  return new Intl.NumberFormat("en-AU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatCurrency = (value: number): string => {
  if (Number.isNaN(value)) return "$0";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

type ShiftBucket = "actual" | "plan" | "excluded";

// ============================================================================
// Plan vs Actual classification. EDIT THIS TABLE to change the behaviour.
// A shift's DRS status decides which cost set the report shows:
//   "actual"   -> use the actual columns, rendered solid.
//   "plan"     -> use the plan columns, rendered dashed.
//   "excluded" -> drop the shift entirely (not shown, not counted).
// Any status not listed falls back to DEFAULT_BUCKET.
// Keep in sync with the backend map in project-shift-costs-api/requestHandler.ts.
// ============================================================================
const SHIFT_STATUS_BUCKET: Record<string, ShiftBucket> = {
  "DRAFT": "plan",             // Draft
  "SUBMITTED-FORMAN": "plan",  // Sent to Crew
  "INPROGRESS": "plan",        // Shift in Progress
  "SUBMITTED-PM": "actual",    // Shift Completed
  "INREVIEW": "actual",        // Shift Review in Progress
  "APPROVED-PM": "actual",     // Review Completed
  "COMPLETED": "actual",       // DRS Completed
  "CANCELLED": "excluded",     // Shift Cancelled
};
const DEFAULT_BUCKET: ShiftBucket = "plan";

const classifyShift = (status: string | null): ShiftBucket =>
  (status && SHIFT_STATUS_BUCKET[status]) || DEFAULT_BUCKET;

const ShiftProfitability = () => {
  const { depotId } = useAppStore();
  const [shifts, setShifts] = useState<ShiftCostItem[]>([]);
  const [summary, setSummary] = useState<ShiftCostSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const handleShiftClick = (drsSummaryId: number | null) => {
    if (drsSummaryId) {
      window.open(`/#/drs/edit/${drsSummaryId}#ShiftProfitability`, '_blank');
    }
  };

  const [allDepotOptions, setAllDepotOptions] = useState<DepotOption[]>([]);
  const [stateOptions, setStateOptions] = useState<StateOption[]>([]);
  const [selectedState, setSelectedState] = useState<string>("");
  const [selectedDepot, setSelectedDepot] = useState<DepotOption | null>(null);
  const [depotsLoaded, setDepotsLoaded] = useState(false);

  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [selectedMarginType, setSelectedMarginType] = useState<MarginType>("");

  // Default to current month/year
  const [selectedYear, setSelectedYear] = useState<number>(moment().year());
  const [selectedMonth, setSelectedMonth] = useState<number>(moment().month() + 1);

  const availableYears = useMemo(() => getAvailableYears(), []);
  const availableMonths = useMemo(() => getAvailableMonths(selectedYear), [selectedYear]);

  // Calculate monthOffset based on selected year and month
  const monthOffset = useMemo(() => {
    return calculateMonthOffset(selectedYear, selectedMonth);
  }, [selectedYear, selectedMonth]);

  const selectedMoment = useMemo(() => {
    return moment({ year: selectedYear, month: selectedMonth - 1 });
  }, [selectedYear, selectedMonth]);

  const daysInMonth = useMemo(() => {
    return selectedMoment.daysInMonth();
  }, [selectedMoment]);

  // When year changes, ensure month is valid
  useEffect(() => {
    const validMonths = getAvailableMonths(selectedYear);
    const isCurrentMonthValid = validMonths.some((m) => m.value === selectedMonth);
    if (!isCurrentMonthValid && validMonths.length > 0) {
      setSelectedMonth(validMonths[0].value);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    const loadDepots = async () => {
      try {
        const res = await apiDepotSearch({ query: "%", isDepotPayrole: false });
        const results = res.data?.depots || [];

        // Map all depots from the nested structure
        const options = results.map((d: any) => ({
          label: d.depot.name,
          value: d.depot.id,
        }));
        setAllDepotOptions(options);

        // Extract unique states
        const statesMap = new Map<string, StateOption>();
        results.forEach((d: any) => {
          if (!statesMap.has(d.state.id)) {
            statesMap.set(d.state.id, {
              label: d.state.name,
              value: d.state.id,
            });
          }
        });
        setStateOptions(Array.from(statesMap.values()).sort((a, b) => a.label.localeCompare(b.label)));

        // Set default to current user's depot if available
        if (depotId) {
          const currentDepot = options.find((d: DepotOption) => d.value === depotId);
          if (currentDepot) {
            setSelectedDepot(currentDepot);
          } else {
            // User's depot not found, default to All Depots
            setSelectedDepot({ label: "All Depots", value: "" });
          }
        } else {
          setSelectedDepot({ label: "All Depots", value: "" });
        }
        setDepotsLoaded(true);
      } catch (err) {
        console.error("Failed to fetch depots", err);
        setSelectedDepot({ label: "All Depots", value: "" });
        setDepotsLoaded(true);
      }
    };
    loadDepots();
  }, [depotId]);

  useEffect(() => {
    // Wait until selectedDepot is initialized
    if (selectedDepot === null) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const params: { monthOffset?: number; depotId?: string; stateId?: string } = {
          monthOffset: monthOffset,
        };
        if (selectedDepot.value) {
          params.depotId = selectedDepot.value;
        }
        if (selectedState) {
          params.stateId = selectedState;
        }

        const res = await apiProjectShiftCosts(params);
        setShifts(res.data?.shifts || []);
        setSummary(res.data?.summary || null);
      } catch (err) {
        console.error("Failed to fetch project shift costs", err);
        setShifts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDepot, selectedState, monthOffset]);

  // Reset project filter when main filters change (new data loaded)
  useEffect(() => {
    setSelectedProject(null);
  }, [selectedDepot, selectedState, monthOffset]);

  // Extract unique projects from shifts for the filter dropdown
  const projectOptions = useMemo(() => {
    const uniqueProjects = new Map<string, ProjectOption>();
    shifts.forEach((shift) => {
      if (!uniqueProjects.has(shift.project_id)) {
        uniqueProjects.set(shift.project_id, {
          label: `${shift.project_id} - ${shift.project_name}`,
          value: shift.project_id,
        });
      }
    });
    return Array.from(uniqueProjects.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [shifts]);

  // Filter shifts by selected project (used for summary calculations)
  const filteredShifts = useMemo(() => {
    if (!selectedProject?.value) {
      return shifts;
    }
    return shifts.filter((shift) => shift.project_id === selectedProject.value);
  }, [shifts, selectedProject]);

  // Process shifts into project-based calendar data
  // Shifts on different days share the same row, shifts on the same day get separate rows
  const projectsData = useMemo(() => {

    // First pass: group shifts by project and day
    const projectDayMap = new Map<string, {
      project_id: string;
      project_name: string;
      dayShifts: Map<number, ShiftDayData[]>; // day -> array of shifts on that day
      mtdRevenue: number;
      mtdCost: number;
      mtdMargin: number;
    }>();

    filteredShifts.forEach((shift) => {
      const bucket = classifyShift(shift.drs_status_id);
      if (bucket === "excluded") return; // cancelled shifts drop out entirely
      const isActual = bucket === "actual";

      const day = moment(shift.shift_date).date();
      // Completed shifts show actual figures; planned shifts show plan figures.
      const rawRevenue = isActual ? shift.project_revenue : shift.plan_project_revenue;
      const revenue = rawRevenue === null || rawRevenue === undefined ? null : (Number(rawRevenue) || 0);
      const cost = Number(isActual ? shift.total_cost : shift.plan_total_cost) || 0;
      const margin = (revenue ?? 0) - cost;

      if (!projectDayMap.has(shift.project_id)) {
        projectDayMap.set(shift.project_id, {
          project_id: shift.project_id,
          project_name: shift.project_name,
          dayShifts: new Map(),
          mtdRevenue: 0,
          mtdCost: 0,
          mtdMargin: 0,
        });
      }

      const project = projectDayMap.get(shift.project_id)!;

      // Group shifts by day
      if (!project.dayShifts.has(day)) {
        project.dayShifts.set(day, []);
      }
      project.dayShifts.get(day)!.push({
        shift_id: shift.shift_id,
        drs_summary_id: shift.drs_summary_id,
        day,
        revenue,
        cost,
        margin,
        isActual,
      });

      // Update MTD totals
      project.mtdRevenue += revenue ?? 0;
      project.mtdCost += cost;
      project.mtdMargin += margin;
    });

    // Second pass: convert to row-based structure
    // Each row contains shifts from different days (row N has the Nth shift from each day)
    let results: ProjectData[] = Array.from(projectDayMap.values()).map((project) => {
      // Find max shifts on any single day
      let maxShiftsPerDay = 0;
      project.dayShifts.forEach((shiftsOnDay) => {
        maxShiftsPerDay = Math.max(maxShiftsPerDay, shiftsOnDay.length);
      });

      // Create rows
      const rows: ProjectRowData[] = [];
      for (let rowIndex = 0; rowIndex < maxShiftsPerDay; rowIndex++) {
        const dayShiftsMap = new Map<number, ShiftDayData>();
        project.dayShifts.forEach((shiftsOnDay, day) => {
          if (shiftsOnDay[rowIndex]) {
            dayShiftsMap.set(day, shiftsOnDay[rowIndex]);
          }
        });
        rows.push({ rowIndex, dayShifts: dayShiftsMap });
      }

      return {
        project_id: project.project_id,
        project_name: project.project_name,
        rows,
        mtdRevenue: project.mtdRevenue,
        mtdCost: project.mtdCost,
        mtdMargin: project.mtdMargin,
      };
    });

    // Filter by margin type
    if (selectedMarginType === "no_margin") {
      results = results.filter((p) => p.mtdMargin === 0);
    } else if (selectedMarginType === "positive") {
      results = results.filter((p) => p.mtdMargin > 0);
    } else if (selectedMarginType === "negative") {
      results = results.filter((p) => p.mtdMargin < 0);
    }

    return results;
  }, [filteredShifts, selectedMarginType]);

  const dayNumbers = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  }, [daysInMonth]);

  // Calculate summary from filtered shifts (so it updates when project filter
  // changes), keeping actual (completed) and plan (not-completed) strictly
  // separate. Cancelled shifts are excluded from both.
  const filteredSummary = useMemo(() => {
    const acc = {
      completedShifts: 0,
      plannedShifts: 0,
      // Actual bucket
      actualRevenue: 0,
      actualLabour: 0,
      actualMaterial: 0,
      actualSubcontractor: 0,
      actualMobileAsset: 0,
      actualCostItems: 0,
      actualOverhead: 0,
      actualTotal: 0,
      totalTonnes: 0,
      totalBinder: 0,
      // Plan bucket
      planRevenue: 0,
      planLabour: 0,
      planMaterial: 0,
      planSubcontractor: 0,
      planMobileAsset: 0,
      planCostItems: 0,
      planOverhead: 0,
      planTotal: 0,
    };

    filteredShifts.forEach((shift) => {
      const bucket = classifyShift(shift.drs_status_id);
      if (bucket === "excluded") return;

      if (bucket === "actual") {
        acc.completedShifts += 1;
        acc.actualRevenue += Number(shift.project_revenue) || 0;
        acc.actualLabour += Number(shift.labour_cost) || 0;
        acc.actualMaterial += Number(shift.material_cost) || 0;
        acc.actualSubcontractor += Number(shift.subcontractor_cost) || 0;
        acc.actualMobileAsset += Number(shift.mobile_asset_cost) || 0;
        acc.actualCostItems += Number(shift.project_cost_items) || 0;
        acc.actualOverhead += Number(shift.overhead_cost) || 0;
        acc.actualTotal += Number(shift.total_cost) || 0;
        acc.totalTonnes += Number(shift.total_tonnes) || 0;
        acc.totalBinder += Number(shift.total_binder) || 0;
      } else {
        acc.plannedShifts += 1;
        acc.planRevenue += Number(shift.plan_project_revenue) || 0;
        acc.planLabour += Number(shift.plan_labour_cost) || 0;
        acc.planMaterial += Number(shift.plan_material_cost) || 0;
        acc.planSubcontractor += Number(shift.plan_subcontractor_cost) || 0;
        acc.planMobileAsset += Number(shift.plan_mobile_asset_cost) || 0;
        acc.planCostItems += Number(shift.plan_project_cost_items) || 0;
        acc.planOverhead += Number(shift.plan_overhead_cost) || 0;
        acc.planTotal += Number(shift.plan_total_cost) || 0;
      }
    });

    return acc;
  }, [filteredShifts]);


  return (
    <Wrapper>
      <ReportHeader>
        <HeaderContent>
          <ReportTitle>
            <TitleIcon>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </TitleIcon>
            <div>
              <MainTitle>Shift Profitability Report</MainTitle>
              <SubTitle>Financial Performance Analysis</SubTitle>
            </div>
          </ReportTitle>
          <ReportMeta>
            <MetaBadge>
              <span>Report Period</span>
              <strong>{selectedMoment.format("MMMM YYYY")}</strong>
            </MetaBadge>
            <MetaBadge>
              <span>Generated</span>
              <strong>{moment().format("DD MMM YYYY, HH:mm")}</strong>
            </MetaBadge>
          </ReportMeta>
        </HeaderContent>
      </ReportHeader>

      <ContentArea>
        <FilterPanel>
          <FilterPanelHeader>
            <FilterIcon>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
            </FilterIcon>
            <span>Filters</span>
          </FilterPanelHeader>
          <FilterGrid>
            <FilterGroup>
              <FilterLabel>State</FilterLabel>
              <Select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
              >
                <option value="">All States</option>
                {stateOptions.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </Select>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Depot</FilterLabel>
              <Select
                value={selectedDepot?.value ?? ""}
                onChange={(e) => {
                  const depot = allDepotOptions.find((d) => d.value === e.target.value);
                  setSelectedDepot(depot || { label: "All Depots", value: "" });
                }}
              >
                <option value="">All Depots</option>
                {allDepotOptions.map((depot) => (
                  <option key={depot.value} value={depot.value}>
                    {depot.label}
                  </option>
                ))}
              </Select>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Year</FilterLabel>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Month</FilterLabel>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {availableMonths.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </Select>
            </FilterGroup>

            {projectOptions.length > 0 && (
              <FilterGroup style={{ gridColumn: "span 2" }}>
                <FilterLabel>Project</FilterLabel>
                <Select
                  value={selectedProject?.value ?? ""}
                  onChange={(e) => {
                    const project = projectOptions.find((p) => p.value === e.target.value);
                    setSelectedProject(project || null);
                  }}
                >
                  <option value="">All Projects</option>
                  {projectOptions.map((project) => (
                    <option key={project.value} value={project.value}>
                      {project.label}
                    </option>
                  ))}
                </Select>
              </FilterGroup>
            )}

            <FilterGroup>
              <FilterLabel>MTD Margin Type</FilterLabel>
              <Select
                value={selectedMarginType}
                onChange={(e) => setSelectedMarginType(e.target.value as MarginType)}
              >
                <option value="">All</option>
                <option value="no_margin">No Margin</option>
                <option value="positive">Positive</option>
                <option value="negative">Negative</option>
              </Select>
            </FilterGroup>
          </FilterGrid>
        </FilterPanel>

      {summary && !loading && (
        <SummarySection>
          <SummaryHeader>
            <SummaryTitle>Financial Summary{selectedProject ? ` - ${selectedProject.label}` : ''}</SummaryTitle>
            <SummaryPeriod>{selectedMoment.format("MMMM YYYY")}</SummaryPeriod>
          </SummaryHeader>
          <LegendRow>
            <LegendItem><LegendSwatch /> Actual (done)</LegendItem>
            <LegendItem><LegendSwatch dashed /> Planned (remaining)</LegendItem>
            <LegendItem>
              <LegendWarning viewBox="0 0 24 24">
                <path d="M12 2L2 22h20L12 2z" fill="#f59e0b" stroke="#b45309" strokeWidth="1.5" />
                <text x="12" y="18" textAnchor="middle" fill="#000" fontSize="12" fontWeight="bold">!</text>
              </LegendWarning>
              Missing data
            </LegendItem>
          </LegendRow>
          <SummaryGrid>
            <SummaryCard>
              <SummaryCardLabel>Total Shifts</SummaryCardLabel>
              <SummaryCardValue>{(filteredSummary.completedShifts + filteredSummary.plannedShifts).toLocaleString()}</SummaryCardValue>
              <BadgeRow>
                <ActualBadge>{filteredSummary.completedShifts} done</ActualBadge>
                <PlanBadge>{filteredSummary.plannedShifts} planned</PlanBadge>
              </BadgeRow>
            </SummaryCard>
            <SummaryCard highlight="revenue">
              <SummaryCardLabel>Total Revenue</SummaryCardLabel>
              <SummaryCardValue>{formatCurrency(filteredSummary.actualRevenue)}</SummaryCardValue>
              <BadgeRow>
                <PlanBadge>{formatCurrency(filteredSummary.planRevenue)} plan</PlanBadge>
              </BadgeRow>
            </SummaryCard>
            <SummaryCard highlight={filteredSummary.actualRevenue - filteredSummary.actualTotal >= 0 ? "profit" : "loss"}>
              <SummaryCardLabel>Net Margin</SummaryCardLabel>
              <SummaryCardValue>
                {formatCurrency(filteredSummary.actualRevenue - filteredSummary.actualTotal)}
              </SummaryCardValue>
              <SummaryCardSubtext>
                {filteredSummary.actualRevenue > 0
                  ? `${((filteredSummary.actualRevenue - filteredSummary.actualTotal) / filteredSummary.actualRevenue * 100).toFixed(1)}% margin`
                  : '0% margin'}
              </SummaryCardSubtext>
              <BadgeRow>
                <PlanBadge>{formatCurrency(filteredSummary.planRevenue - filteredSummary.planTotal)} plan</PlanBadge>
              </BadgeRow>
            </SummaryCard>
            <SummaryCard highlight="cost">
              <SummaryCardLabel>Total Cost</SummaryCardLabel>
              <SummaryCardValue>{formatCurrency(filteredSummary.actualTotal)}</SummaryCardValue>
              <BadgeRow>
                <PlanBadge>{formatCurrency(filteredSummary.planTotal)} plan</PlanBadge>
              </BadgeRow>
            </SummaryCard>
          </SummaryGrid>
          <SummaryGridSecondary>
            <SummaryCardSmall>
              <SummaryCardLabel>Material</SummaryCardLabel>
              <SummaryCardValue>{formatCurrency(filteredSummary.actualMaterial)}</SummaryCardValue>
              <PlanBadge>{formatCurrency(filteredSummary.planMaterial)} plan</PlanBadge>
            </SummaryCardSmall>
            <SummaryCardSmall>
              <SummaryCardLabel>Labour</SummaryCardLabel>
              <SummaryCardValue>{formatCurrency(filteredSummary.actualLabour)}</SummaryCardValue>
              <PlanBadge>{formatCurrency(filteredSummary.planLabour)} plan</PlanBadge>
            </SummaryCardSmall>
            <SummaryCardSmall>
              <SummaryCardLabel>Mobile Assets</SummaryCardLabel>
              <SummaryCardValue>{formatCurrency(filteredSummary.actualMobileAsset)}</SummaryCardValue>
              <PlanBadge>{formatCurrency(filteredSummary.planMobileAsset)} plan</PlanBadge>
            </SummaryCardSmall>
            <SummaryCardSmall>
              <SummaryCardLabel>Subcontractor</SummaryCardLabel>
              <SummaryCardValue>{formatCurrency(filteredSummary.actualSubcontractor)}</SummaryCardValue>
              <PlanBadge>{formatCurrency(filteredSummary.planSubcontractor)} plan</PlanBadge>
            </SummaryCardSmall>
            <SummaryCardSmall>
              <SummaryCardLabel>Other Custom Items</SummaryCardLabel>
              <SummaryCardValue>{formatCurrency(filteredSummary.actualCostItems)}</SummaryCardValue>
              <PlanBadge>{formatCurrency(filteredSummary.planCostItems)} plan</PlanBadge>
            </SummaryCardSmall>
            <SummaryCardSmall>
              <SummaryCardLabel>Overhead</SummaryCardLabel>
              <SummaryCardValue>{formatCurrency(filteredSummary.actualOverhead)}</SummaryCardValue>
              <PlanBadge>{formatCurrency(filteredSummary.planOverhead)} plan</PlanBadge>
            </SummaryCardSmall>
            <SummaryCardSmall>
              <SummaryCardLabel>Tonnes Total</SummaryCardLabel>
              <SummaryCardValue>{formatNumber(filteredSummary.totalTonnes) || '0'}</SummaryCardValue>
            </SummaryCardSmall>
            <SummaryCardSmall>
              <SummaryCardLabel>Binder Total</SummaryCardLabel>
              <SummaryCardValue>{formatNumber(filteredSummary.totalBinder) || '0'}</SummaryCardValue>
            </SummaryCardSmall>
          </SummaryGridSecondary>
        </SummarySection>
      )}

        {loading || !depotsLoaded ? (
          <LoadingMessage>
            <LoadingSpinner />
            <span>Loading financial data...</span>
          </LoadingMessage>
        ) : (
          <TableSection>
            <TableHeader>
              <TableTitleRow>
                <TableIcon>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="3" y1="9" x2="21" y2="9" />
                    <line x1="9" y1="21" x2="9" y2="9" />
                  </svg>
                </TableIcon>
                <TableTitle>Project Profitability Details</TableTitle>
              </TableTitleRow>
              <TableSubtitle>Daily breakdown by project with revenue, cost, and margin analysis</TableSubtitle>
            </TableHeader>
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <HeaderCell rowSpan={2}>Project Number</HeaderCell>
                    <HeaderCell rowSpan={2}>Project Name</HeaderCell>
                    <HeaderCell rowSpan={2}>Metric</HeaderCell>
                    <MonthHeader colSpan={daysInMonth}>
                      {selectedMoment.format("MMMM YYYY")}
                    </MonthHeader>
                    <HeaderCell rowSpan={2}>MTD Total</HeaderCell>
                  </tr>
                  <tr>
                    {dayNumbers.map((day) => (
                      <DayHeaderCell key={day}>{day}</DayHeaderCell>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {projectsData.length === 0 ? (
                    <tr>
                      <EmptyStateCell colSpan={daysInMonth + 4}>
                        <EmptyStateContent>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                            <polyline points="13 2 13 9 20 9" />
                          </svg>
                          <span>No data found for the selected period</span>
                        </EmptyStateContent>
                      </EmptyStateCell>
                    </tr>
                  ) : (
                    projectsData.map((project) => (
                      <React.Fragment key={project.project_id}>
                        {project.rows.map((row, rowIndex) => {
                          const isLastRow = rowIndex === project.rows.length - 1;
                          const totalRows = project.rows.length * 3;
                          return (
                            <React.Fragment key={rowIndex}>
                              {/* Revenue Row */}
                              <DataRow>
                                {rowIndex === 0 && (
                                  <>
                                    <ProjectCell rowSpan={totalRows}>{project.project_id}</ProjectCell>
                                    <ProjectNameCell rowSpan={totalRows}>{project.project_name}</ProjectNameCell>
                                  </>
                                )}
                                <LabelCell type="revenue">Revenue</LabelCell>
                                {dayNumbers.map((day) => {
                                  const shift = row.dayShifts.get(day);
                                  const hasRevenue = shift && shift.revenue !== null && shift.revenue !== undefined;
                                  const isNullRevenue = shift && shift.revenue === null;
                                  const isPlan = !!shift && !shift.isActual;

                                  if (isNullRevenue) {
                                    return (
                                      <WarningCell key={day} plan={isPlan} onClick={() => handleShiftClick(shift.drs_summary_id)}>
                                        <WarningIcon viewBox="0 0 24 24">
                                          <path d="M12 2L2 22h20L12 2z" fill="#f59e0b" stroke="#b45309" strokeWidth="1.5"/>
                                          <text x="12" y="18" textAnchor="middle" fill="#000" fontSize="12" fontWeight="bold">!</text>
                                        </WarningIcon>
                                      </WarningCell>
                                    );
                                  }

                                  return (
                                    <ClickableDataCell
                                      key={day}
                                      clickable={!!hasRevenue}
                                      plan={isPlan}
                                      onClick={() => hasRevenue && handleShiftClick(shift.drs_summary_id)}
                                    >
                                      {hasRevenue ? formatCurrency(shift.revenue!) : ""}
                                    </ClickableDataCell>
                                  );
                                })}
                                {rowIndex === 0 && (
                                  <MTDCell type="revenue" rowSpan={totalRows}>
                                    <div>{formatCurrency(project.mtdRevenue)}</div>
                                    <MTDSubValue type="cost">{formatCurrency(project.mtdCost)}</MTDSubValue>
                                    <MTDSubValue type={project.mtdMargin >= 0 ? "profit" : "loss"}>
                                      {formatCurrency(project.mtdMargin)}
                                    </MTDSubValue>
                                  </MTDCell>
                                )}
                              </DataRow>

                              {/* Cost Row */}
                              <DataRow>
                                <LabelCell type="cost">Cost</LabelCell>
                                {dayNumbers.map((day) => {
                                  const shift = row.dayShifts.get(day);
                                  const hasData = shift && shift.cost;
                                  return (
                                    <ClickableDataCell
                                      key={day}
                                      clickable={!!hasData}
                                      plan={!!shift && !shift.isActual}
                                      onClick={() => hasData && handleShiftClick(shift.drs_summary_id)}
                                    >
                                      {hasData ? formatNumber(shift.cost) : ""}
                                    </ClickableDataCell>
                                  );
                                })}
                              </DataRow>

                              {/* Margin Row */}
                              <DataRow isLastInGroup={isLastRow}>
                                <LabelCell type="margin">Margin</LabelCell>
                                {dayNumbers.map((day) => {
                                  const shift = row.dayShifts.get(day);
                                  if (!shift || (shift.revenue === 0 && shift.cost === 0)) {
                                    return <ClickableDataCell key={day} clickable={false}></ClickableDataCell>;
                                  }
                                  const isPositive = shift.margin >= 0;
                                  return (
                                    <ClickableMarginCell
                                      key={day}
                                      isPositive={isPositive}
                                      plan={!shift.isActual}
                                      onClick={() => handleShiftClick(shift.drs_summary_id)}
                                    >
                                      {formatCurrency(shift.margin)}
                                    </ClickableMarginCell>
                                  );
                                })}
                              </DataRow>
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </Table>
            </TableContainer>
          </TableSection>
        )}
      </ContentArea>
    </Wrapper>
  );
};

// Main wrapper with subtle background
const Wrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
`;

// Report Header
const ReportHeader = styled.header`
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 24px 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  max-width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const ReportTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TitleIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0a9688 0%, #0d7d72 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const MainTitle = styled.h1`
  color: white;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
`;

const SubTitle = styled.p`
  color: #94a3b8;
  font-size: 14px;
  margin: 4px 0 0 0;
`;

const ReportMeta = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const MetaBadge = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;

  span {
    color: #94a3b8;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  strong {
    color: white;
    font-size: 14px;
    font-weight: 600;
  }
`;

// Content Area
const ContentArea = styled.main`
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Filter Panel
const FilterPanel = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
`;

const FilterPanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
  color: #475569;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilterIcon = styled.span`
  color: #64748b;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 10px 14px;
  min-width: 100%;
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    background: white;
  }

  &:focus {
    outline: none;
    border-color: #0a9688;
    box-shadow: 0 0 0 3px rgba(10, 150, 136, 0.1);
    background: white;
  }
`;

// Loading State
const LoadingMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 80px 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  color: #64748b;
  font-size: 15px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #0a9688;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Summary Section
const SummarySection = styled.div`
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
`;

const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SummaryTitle = styled.h3`
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: #0a9688;
    border-radius: 2px;
  }
`;

const SummaryPeriod = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.08);
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div<{ highlight?: "revenue" | "cost" | "profit" | "loss" }>`
  background: ${(props) => {
    switch (props.highlight) {
      case "revenue":
        return "linear-gradient(135deg, #0a9688 0%, #0d7d72 100%)";
      case "cost":
        return "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)";
      case "profit":
        return "linear-gradient(135deg, #16a34a 0%, #15803d 100%)";
      case "loss":
        return "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)";
      default:
        return "rgba(255, 255, 255, 0.05)";
    }
  }};
  border: 1px solid ${(props) => props.highlight ? "transparent" : "rgba(255, 255, 255, 0.1)"};
  border-radius: 12px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }
`;

const SummaryCardLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SummaryCardValue = styled.span`
  color: white;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const SummaryCardSubtext = styled.span`
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.2);
  padding: 4px 8px;
  border-radius: 4px;
  width: fit-content;
`;

// Legend explaining the actual/plan/missing states.
const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  font-weight: 600;
`;

const LegendSwatch = styled.span<{ dashed?: boolean }>`
  width: 18px;
  height: 14px;
  border-radius: 3px;
  ${(props) =>
    props.dashed
      ? `background: transparent; border: 1.5px dashed rgba(255, 255, 255, 0.7);`
      : `background: rgba(255, 255, 255, 0.85); border: 1px solid rgba(255, 255, 255, 0.85);`}
`;

const LegendWarning = styled.svg`
  width: 16px;
  height: 16px;
`;

// Actual/plan value badges shown under summary card values. Plan is dashed to
// mirror the dashed plan cells in the grid.
const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
`;

const ActualBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: white;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 3px 8px;
  border-radius: 6px;
  width: fit-content;
`;

const PlanBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  background: transparent;
  border: 1.5px dashed rgba(255, 255, 255, 0.55);
  padding: 3px 8px;
  border-radius: 6px;
  width: fit-content;
  margin-top: 4px;
`;

const SummaryGridSecondary = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCardSmall = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  ${SummaryCardValue} {
    font-size: 16px;
  }
`;

// Table Section
const TableSection = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const TableHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const TableTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TableIcon = styled.span`
  color: #0a9688;
`;

const TableTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const TableSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 6px 0 0 32px;
`;

const TableContainer = styled.div`
  overflow: auto;
  max-height: calc(100vh - 400px);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  white-space: nowrap;

  th, td {
    padding: 10px 12px;
    text-align: center;
    border-bottom: 1px solid #e2e8f0;
  }
`;

const HeaderCell = styled.th`
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  color: white;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
  padding: 14px 16px;
  position: sticky;
  top: 0;
  z-index: 10;

  &:first-child {
    min-width: 110px;
    position: sticky;
    left: 0;
    z-index: 20;
  }

  &:nth-child(2) {
    min-width: 180px;
    position: sticky;
    left: 110px;
    z-index: 20;
  }

  &:nth-child(3) {
    min-width: 80px;
    position: sticky;
    left: 290px;
    z-index: 20;
  }

  &:last-child {
    min-width: 110px;
    text-align: right;
  }
`;

const MonthHeader = styled.th`
  background: linear-gradient(180deg, #334155 0%, #1e293b 100%);
  color: #94a3b8;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const DayHeaderCell = styled.th`
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  color: white;
  font-weight: 600;
  font-size: 11px;
  min-width: 65px;
  padding: 10px 8px;
  position: sticky;
  top: 40px;
  z-index: 10;
`;

const DataRow = styled.tr<{ isLastInGroup?: boolean }>`
  transition: background 0.15s ease;

  &:hover {
    background: #f8fafc;
  }

  ${(props) => props.isLastInGroup && `
    border-bottom: 3px solid #e2e8f0;
  `}
`;

const ProjectCell = styled.td`
  background: #f1f5f9;
  font-weight: 700;
  font-size: 13px;
  color: #0f172a;
  text-align: left;
  vertical-align: middle;
  padding: 12px 16px;
  border-right: 1px solid #e2e8f0;
  position: sticky;
  left: 0;
  z-index: 5;
`;

const ProjectNameCell = styled.td`
  background: #f1f5f9;
  text-align: left;
  vertical-align: middle;
  font-weight: 500;
  color: #475569;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 12px 16px;
  border-right: 1px solid #e2e8f0;
  position: sticky;
  left: 110px;
  z-index: 5;
`;

const LabelCell = styled.td<{ type?: "revenue" | "cost" | "margin" }>`
  background: ${(props) => {
    switch (props.type) {
      case "revenue": return "#f0fdf4";
      case "cost": return "#fef2f2";
      case "margin": return "#f0f9ff";
      default: return "#f8fafc";
    }
  }};
  text-align: left;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${(props) => {
    switch (props.type) {
      case "revenue": return "#15803d";
      case "cost": return "#dc2626";
      case "margin": return "#0369a1";
      default: return "#64748b";
    }
  }};
  padding: 10px 12px;
  border-right: 1px solid #e2e8f0;
  position: sticky;
  left: 290px;
  z-index: 5;
`;

const ClickableDataCell = styled.td<{ clickable: boolean; plan?: boolean }>`
  text-align: right;
  background: ${(props) => (props.plan ? "#f8fafc" : "white")};
  color: #475569;
  font-variant-numeric: tabular-nums;
  padding: 10px 12px;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: all 0.15s ease;

  ${(props) => props.plan && `
    outline: 1.5px dashed #94a3b8;
    outline-offset: -4px;
  `}

  ${(props) =>
    props.clickable &&
    `
    &:hover {
      background: #e0f2fe;
      color: #0369a1;
      font-weight: 600;
    }
  `}
`;

const WarningCell = styled.td<{ plan?: boolean }>`
  text-align: center;
  background: #fef3c7;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.15s ease;

  ${(props) => props.plan && `
    outline: 1.5px dashed #b45309;
    outline-offset: -4px;
  `}

  &:hover {
    background: #fde68a;
  }
`;

const WarningIcon = styled.svg`
  width: 22px;
  height: 22px;
  vertical-align: middle;
`;

const ClickableMarginCell = styled.td<{ isPositive: boolean; plan?: boolean }>`
  text-align: right;
  background: ${(props) => (props.isPositive ? "#dcfce7" : "#fee2e2")};
  color: ${(props) => (props.isPositive ? "#166534" : "#dc2626")};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.15s ease;

  ${(props) => props.plan && `
    outline: 1.5px dashed #94a3b8;
    outline-offset: -4px;
  `}

  &:hover {
    background: ${(props) => (props.isPositive ? "#bbf7d0" : "#fecaca")};
    transform: scale(1.02);
  }
`;

const MTDCell = styled.td<{ type?: "revenue" | "cost" }>`
  text-align: right;
  vertical-align: middle;
  background: ${(props) => {
    switch (props.type) {
      case "revenue": return "#f0fdf4";
      case "cost": return "#fef2f2";
      default: return "#f8fafc";
    }
  }};
  font-weight: 700;
  color: ${(props) => {
    switch (props.type) {
      case "revenue": return "#15803d";
      case "cost": return "#dc2626";
      default: return "#1e293b";
    }
  }};
  font-variant-numeric: tabular-nums;
  padding: 10px 16px;
  border-left: 2px solid #e2e8f0;
`;

const MTDSubValue = styled.div<{ type: "cost" | "profit" | "loss" }>`
  font-size: 11px;
  font-weight: 600;
  margin-top: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${(props) => {
    switch (props.type) {
      case "cost": return "#fef2f2";
      case "profit": return "#dcfce7";
      case "loss": return "#fee2e2";
    }
  }};
  color: ${(props) => {
    switch (props.type) {
      case "cost": return "#dc2626";
      case "profit": return "#166534";
      case "loss": return "#dc2626";
    }
  }};
`;

const EmptyStateCell = styled.td`
  padding: 60px 20px;
  background: #f8fafc;
`;

const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #94a3b8;
  font-size: 14px;
`;

export default ShiftProfitability;
